import { useRef } from "react";
import type Button from "../../../src/models/Button";
import type ButtonChannel from "../../../src/models/ButtonChannel";
import type Consumer from "../../../src/models/Consumer";
import { assessConnection } from "../../../src/models/connectionCreation";
import { ChannelType, getChannelTypePicture, specialChannelTypes } from "../../../src/models/ChannelType";
import { useOnChangeWatcher } from "../../../src/models/useOnChangeWatcher";
import { useChildConnectionEndpoints } from "../../../src/connectionAnalyser/useChildConnectionEndpoints";
import { itx } from "../../../src/translationItx/itx";
import { draggedConsumer, hasConsumerDragType } from "./C1_ButtonEditorView";
import c1_state from "../model/C1_State";
import { C1_EventKey } from "../C1_EventKey";

/** Uses the same connection policy and canonical channel as the graphical button editor. */
export function assignListChannelConsumer(button: Button, channel: ButtonChannel, consumer: Consumer): boolean {
  if (channel.parentDevice !== button || !assessConnection(channel, consumer).allowed) return false;
  button.assignConsumer(channel.tag, consumer);
  c1_state.eventLogger.addEvent(C1_EventKey.ConnectionCreated, {
    buttonId: button.id,
    channelTag: channel.tag,
    channelType: channel.channelType,
    consumerId: consumer.id,
    method: "dragAndDrop",
    source: "channelList",
  });
  return true;
}

function ChannelRow({ button, channel }: { button: Button; channel: ButtonChannel }) {
  useOnChangeWatcher(channel);
  useOnChangeWatcher(channel.connections);
  const label = channel.getChannelNameForUI();
  const select = () => { state.selectionManager.selectedObject = channel; };
  return (
    <div className="C1_sensorChannel" data-testid={`button-channel-row-${channel.id}`}>
      <div className="C1_sensorChannelControl C1_buttonEditorAuxiliaryChannel">
      <div className="C1_sensorChannelTarget C1_buttonChannelTarget" role="button" tabIndex={0}
        aria-label={label}
        data-connection-object-id={channel.id} data-connection-interaction="prefer-control"
        onClick={select}
        onKeyDown={event => {
          if (event.key === "Enter" || event.key === " ") { event.preventDefault(); select(); }
        }}
        onDragOver={event => {
          if (!hasConsumerDragType(event.dataTransfer)) return;
          event.preventDefault(); event.stopPropagation(); event.dataTransfer.dropEffect = "copy";
        }}
        onDrop={event => {
          event.preventDefault(); event.stopPropagation();
          const consumer = draggedConsumer(event.dataTransfer);
          if (consumer && assignListChannelConsumer(button, channel, consumer)) state.undoManager.userInteractionEnded();
        }}>
        <img className="C1_buttonAuxiliaryIcon" src={getChannelTypePicture(channel.channelType)} alt="" draggable={false} />
        <span className="C1_buttonAuxiliaryLabel">
          <span className="C1_sensorChannelName">{label}</span>
        </span>
      </div>
      </div>
    </div>
  );
}

/** Lists actual device channels, including sensor channels without physical rocker geometry. */
export function C1_ButtonChannelList({ button }: { button: Button }) {
  useOnChangeWatcher(button);
  useOnChangeWatcher(button.channels);
  const endpoints = useRef<HTMLDivElement>(null);
  useChildConnectionEndpoints(state.connectionAnalyser, endpoints, "button-editor");
  // Match the functional-channel selection in KnxButtonTable, without its status LED section.
  const channels = button.buttonChannels.asArray().filter(channel =>
    !channel.isDeleted && !channel.isStatusChannel && !channel.isLogicChannel &&
    !specialChannelTypes.includes(channel.channelType) && channel.channelType !== ChannelType.Disabled &&
    (!channel.isActorChannel || channel.channelType === ChannelType.TemperatureMeasurement));
  return <div ref={endpoints} className="C1_sensorChannels" data-testid="button-editor-channel-list">
    <h3 className="dockTitle">{button.isSensor ? itx("c1.sensorEditor.channels", "Kanäle") : itx("components.plural.ButtonChannel", "Tastenkanäle")}</h3>
    {channels.map(channel => <ChannelRow key={channel.id} button={button} channel={channel} />)}
    {channels.length === 0 && <p>{button.isSensor ? itx("c1.sensorEditor.noChannels", "Noch keine Kanäle vorhanden.") : itx("c1.buttonEditor.noChannels", "Noch keine Tastenkanäle vorhanden.")}</p>}
  </div>;
}
